import {
  getAdditionalDescription,
  getDescription,
  getDisabilityFlexSinglePayment,
  getHouseContentsDeductible,
  getIssueDate,
  getShortTermExpiryText,
} from "Utils/descriptions";
import { coverageGroupName, showCoverageGroup } from "Utils/productUtils";
import {
  HeaderProductInfoAndPriceRow,
  HeaderProductInfoRow,
  HeaderWrapper,
  IconWrapper,
  StyledDesc,
  Title,
} from "./styled";

import { StyledRow } from "App/global-style";
import { useUpsellFeature } from "Components/Features/Upsell/hooks/useUpsellFeature";
import { NumberValue, VALUE_TYPE } from "Components/NumberValue";
import { PRESENTATION } from "Components/NumberValue/styled";
import { useCurrentCart } from "Store/cart";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { ProductIcon } from "Utils/icons";
import { getProductName } from "Utils/nameUtils/nameUtils";
import { tfaPerMonth } from "Utils/priceUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React from "react";
import { CardHeaderPrice, PriceWrapper } from "./CardHeaderPrice";
import { CustomerRecommendationMessage } from "./CustomerRecommendationMessage";

interface Props {
  readonly product: InsuranceProduct;
  readonly productIcon: boolean;
  readonly displayIssueDate?: boolean;
}

export const CardHeader = ({
  product,
  productIcon,
  displayIssueDate,
}: Props): React.ReactElement => {
  const [productText, lang] = useProductTexts(getFMSProductName(product.name));
  const productName = getProductName(product);
  const cart = useCurrentCart();
  const isChildCoverageExtraHalfGActive = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_2464_ENABLE_NEWCHILD_HALF_G,
  );
  const isUpsellActive = useUpsellFeature();

  const monthlyTfa = tfaPerMonth(product);

  return (
    <StyledRow className="insurance-details-card__header">
      {productIcon && (
        <IconWrapper className="svg" background="transparent">
          <ProductIcon product={product} label={productName} />
        </IconWrapper>
      )}
      <HeaderWrapper>
        <HeaderProductInfoAndPriceRow>
          <HeaderProductInfoRow>
            <StyledRow>
              <Title>
                {productName}
                {showCoverageGroup(product, isChildCoverageExtraHalfGActive) &&
                  coverageGroupName(product, productText, lang)}
              </Title>
            </StyledRow>
          </HeaderProductInfoRow>
          <CardHeaderPrice product={product} />
        </HeaderProductInfoAndPriceRow>
        <HeaderProductInfoAndPriceRow>
          <HeaderProductInfoRow>
            {isUpsellActive && monthlyTfa > 0 && (
              <StyledRow>
                <StyledDesc>Trafikkforsikringsavgift</StyledDesc>
              </StyledRow>
            )}
            <StyledRow>
              <StyledDesc className="data-hj-suppress">
                {getDescription({ product, cart, productText, lang })}
              </StyledDesc>
            </StyledRow>
            <StyledRow>
              <StyledDesc>
                {getAdditionalDescription({
                  product,
                  productText,
                  lang,
                  isChildCoverageExtraHalfGActive,
                })}
              </StyledDesc>
            </StyledRow>
            {product.name === INSURANCE_NAME_VALUE.HOUSE_CONTENTS && (
              <StyledRow>
                <StyledDesc>{getHouseContentsDeductible(product)}</StyledDesc>
              </StyledRow>
            )}
            {displayIssueDate === true && (
              <StyledRow>
                <StyledDesc>{getIssueDate({ product })}</StyledDesc>
              </StyledRow>
            )}
            {product.name === INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN && (
              <StyledRow>
                <StyledDesc>{getShortTermExpiryText(product)}</StyledDesc>
              </StyledRow>
            )}
            {product.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX && (
              <StyledRow>
                <StyledDesc>
                  {getDisabilityFlexSinglePayment(product, productText, lang)}
                </StyledDesc>
              </StyledRow>
            )}
          </HeaderProductInfoRow>
          {isUpsellActive && monthlyTfa > 0 && (
            <PriceWrapper>
              <p>
                <NumberValue
                  value={monthlyTfa}
                  type={VALUE_TYPE.NOK_MONTH}
                  presentation={PRESENTATION.ADDITION}
                />
              </p>
            </PriceWrapper>
          )}
        </HeaderProductInfoAndPriceRow>
        <CustomerRecommendationMessage product={product} />
      </HeaderWrapper>
    </StyledRow>
  );
};
